import * as React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChevronDown,
    faCheck,
    faSpinner
} from '@fortawesome/pro-regular-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import {
    setSelectedFacilityID,
    searchFacilitiesForAsyncSelect
} from '../../actions/commonActions';
import * as types from '../../actions/actionTypes';
import { debounce } from 'lodash';
import {
    getSelectedFacilityID,
    getFacilities
} from '../../reducers/facilitiesReducer';
import {
    getUser,
    getActiveLocalCountry
} from '../../reducers/manageUserReducer';
import { getUsersActiveCountry } from '../../reducers/userReducer';
import { IfacilityWithoutBuildings, Ioption, Ifacility } from '../../models';
import { constants } from '../../constants/constants';
import { formatFacilityStreet } from '../../helpers/stringHelpers';

// Move this outside component to avoid recreation on every render
const generateFacilityOption = (
    facility: IfacilityWithoutBuildings
): Ioption => {
    const street = formatFacilityStreet(
        facility.address,
        facility.address2,
        facility.countryID
    );
    let nameWithAddress = `${facility.name} | ${street} ${facility.city} ${facility.state} ${facility.postalCode}`;

    return {
        value: facility.id,
        label: nameWithAddress
    };
};

const HeaderFacilitySelector = () => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [isSearchMode, setIsSearchMode] = React.useState(false);
    const [searchTerm, setSearchTerm] = React.useState('');
    const [searchOptions, setSearchOptions] = React.useState<Ioption[]>([]);
    const [isSearchLoading, setIsSearchLoading] = React.useState(false);
    const [shouldTriggerSearch, setShouldTriggerSearch] = React.useState(false);
    const dropdownRef = React.useRef<HTMLDivElement>(null);
    const inputRef = React.useRef<HTMLInputElement>(null);

    const dispatch = useDispatch();
    const user = useSelector(getUser);
    const selectedFacilityID = useSelector(getSelectedFacilityID); // Single selected ID
    const allFacilities = useSelector(getFacilities); // Get all facilities from Redux state

    // Use Redux selectors for active country (matching CountrySelector pattern)
    const usersActiveCountry = useSelector(getUsersActiveCountry);
    const usersActiveLocalCountry = useSelector(getActiveLocalCountry);

    // Memoize active country to avoid recalculation
    const activeCountryId = React.useMemo(() => {
        if (usersActiveLocalCountry !== '') {
            return usersActiveLocalCountry;
        } else {
            return usersActiveCountry;
        }
    }, [usersActiveLocalCountry, usersActiveCountry]);

    // Centralized cleanup function to avoid redundancy
    const resetSearchState = React.useCallback(() => {
        setIsSearchMode(false);
        setSearchTerm('');
        setSearchOptions([]);
        setIsSearchLoading(false);
        setShouldTriggerSearch(false);
    }, []);

    // Function to save facilities to Redux (similar to SearchFacilityForm)
    const saveFacilities = React.useCallback(
        (facilities: Ifacility[]) => {
            if (!facilities) {
                console.warn('No facilities received');
                return;
            }

            dispatch({
                type: types.GET_FACILITIES_SUCCESS,
                facilities
            });
        },
        [dispatch]
    );

    // Callback to handle search results and update dropdown options
    const handleSearchResults = React.useCallback((options: Ioption[]) => {
        setSearchOptions(options || []);
        setIsSearchLoading(false); // Stop loading when results come back
    }, []);

    // Debounced search function for country-based facility search
    // Use React.useRef to maintain stable debounced function across renders
    const debouncedSearch = React.useRef(
        debounce(
            (
                term: string,
                countryId: string,
                onResults: (options: Ioption[]) => void,
                onSave: (facilities: Ifacility[]) => void,
                dispatchFn: any
            ) => {
                if (term.length >= 3) {
                    const searchAction = searchFacilitiesForAsyncSelect(
                        term,
                        countryId,
                        null, // quantityOfAssets - not needed
                        onResults, // Callback to handle search options
                        onSave // Save facilities to Redux
                    );
                    dispatchFn(searchAction);
                }
            },
            constants.tableSearchDebounceTime
        )
    ).current;

    const performSearch = React.useCallback(
        (term: string) => {
            if (term.length >= 3) {
                setIsSearchLoading(true);
                debouncedSearch(
                    term,
                    activeCountryId,
                    handleSearchResults,
                    saveFacilities,
                    dispatch
                );
            } else {
                // Clear search options if less than 3 characters
                setSearchOptions([]);
                setIsSearchLoading(false);
            }
        },
        // eslint-disable-next-line
        [activeCountryId, handleSearchResults, saveFacilities, dispatch]
    );

    // Get user's assigned facility IDs for comparison
    const userFacilityIds = React.useMemo(() => {
        return user.facilities.map(facility => facility.id);
    }, [user.facilities]);

    // Memoize facilities array to avoid Object.values on each render
    const facilitiesArray = React.useMemo(() => {
        return Object.values(allFacilities).filter(
            (facility: IfacilityWithoutBuildings) => !facility.isDeleted
        );
    }, [allFacilities]);

    // Pre-compute facility options with their display labels for performance
    const facilitiesWithOptions = React.useMemo(() => {
        return facilitiesArray.map(facility => ({
            ...facility,
            option: generateFacilityOption(facility)
        }));
    }, [facilitiesArray]);

    // Memoize selected facility display to avoid re-searching facilities
    const selectedFacilityDisplay = React.useMemo(() => {
        if (!selectedFacilityID) return 'No Facility Selected';

        // Find the selected facility from pre-computed facilities with options
        const selectedFacilityWithOption = facilitiesWithOptions.find(
            facility => facility.id === selectedFacilityID
        );

        if (!selectedFacilityWithOption) return 'No Facility Available';

        // Check if selected facility is from the active country
        if (selectedFacilityWithOption.countryID !== activeCountryId) {
            return 'No Facility Selected (Different Country)';
        }

        // Return pre-computed option label
        return selectedFacilityWithOption.option.label;
    }, [selectedFacilityID, facilitiesWithOptions, activeCountryId]);

    // Show all facilities but sort assigned ones to the top
    const facilitiesByCountry = React.useMemo(() => {
        interface CountryGroup {
            countryName: string;
            facilities: (IfacilityWithoutBuildings & { option: Ioption })[];
        }

        const grouped: { [countryId: string]: CountryGroup } = {};

        // Get facilities for the active country from pre-computed array
        const activeFacilities = facilitiesWithOptions.filter(
            facility => facility.countryID === activeCountryId
        );

        // Use search results if available, otherwise filter locally
        let filteredFacilities: (IfacilityWithoutBuildings & {
            option: Ioption;
        })[] = [];

        if (
            isSearchMode &&
            searchTerm &&
            searchTerm.length >= 3 &&
            searchOptions.length > 0
        ) {
            // Use facilities from search results
            const searchResultFacilityIds = searchOptions.map(
                option => option.value
            );
            filteredFacilities = facilitiesWithOptions.filter(
                facility =>
                    searchResultFacilityIds.includes(facility.id) &&
                    facility.countryID === activeCountryId
            );
        } else if (isSearchMode && searchTerm && searchTerm.length < 3) {
            // For less than 3 characters, filter locally
            filteredFacilities = activeFacilities.filter(facility =>
                facility.option.label
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase())
            );
        } else {
            // Default: show all active facilities
            filteredFacilities = activeFacilities;
        }

        // Sort facilities: assigned ones first, then alphabetically within each group
        filteredFacilities = filteredFacilities.sort((a, b) => {
            const aIsAssigned = userFacilityIds.includes(a.id);
            const bIsAssigned = userFacilityIds.includes(b.id);

            // If both are assigned or both are unassigned, sort alphabetically by name
            if (aIsAssigned === bIsAssigned) {
                return a.name.localeCompare(b.name);
            }

            // Assigned facilities come first
            return bIsAssigned ? 1 : -1;
        });

        filteredFacilities.forEach(facility => {
            const countryId = facility.countryID;
            if (!grouped[countryId]) {
                const country = constants.countries[countryId];
                grouped[countryId] = {
                    countryName: country ? country.label : 'Unknown Country',
                    facilities: []
                };
            }
            grouped[countryId].facilities.push(facility);
        });

        return grouped;
    }, [
        facilitiesWithOptions,
        userFacilityIds,
        activeCountryId,
        isSearchMode,
        searchTerm,
        searchOptions
    ]);

    // Handle clicking outside to close dropdown and exit search mode
    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
                resetSearchState();
            }
        };

        if (isOpen || isSearchMode) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, isSearchMode, resetSearchState]);

    // Focus input when entering search mode
    React.useEffect(() => {
        if (isSearchMode && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select(); // Select all text for easy replacement
        }
    }, [isSearchMode]);

    // Trigger search when searchTerm changes AND shouldTriggerSearch is true
    React.useEffect(() => {
        if (isSearchMode && searchTerm && shouldTriggerSearch) {
            performSearch(searchTerm);
        }
    }, [searchTerm, isSearchMode, performSearch, shouldTriggerSearch]);

    const handleFacilitySelect = React.useCallback(
        (facilityId: string) => {
            dispatch(setSelectedFacilityID(facilityId));
            setIsOpen(false);
            resetSearchState();
        },
        [dispatch, resetSearchState]
    );

    const toggleDropdown = React.useCallback(() => {
        setIsOpen(!isOpen);
    }, [isOpen]);

    const handleTextClick = React.useCallback(() => {
        setIsSearchMode(true);
        setSearchTerm(selectedFacilityDisplay);
        setShouldTriggerSearch(false); // Don't trigger search when entering search mode
        if (!isOpen) {
            setIsOpen(true); // Open dropdown when starting search
        }
    }, [selectedFacilityDisplay, isOpen]);

    const handleInputChange = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            setSearchTerm(event.target.value);
            setShouldTriggerSearch(true); // Enable search triggering for user input
            if (!isOpen) {
                setIsOpen(true); // Open dropdown if not already open
            }
        },
        [isOpen]
    );

    const handleInputKeyDown = React.useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
                resetSearchState();
            } else if (event.key === 'Enter') {
                // If there's exactly one filtered result, select it
                const allFilteredFacilities = Object.values(
                    facilitiesByCountry
                ).flatMap(group => group.facilities);
                if (allFilteredFacilities.length === 1) {
                    handleFacilitySelect(allFilteredFacilities[0].id);
                }
            }
        },
        [facilitiesByCountry, handleFacilitySelect, resetSearchState]
    );

    return (
        <div className="header-facility-selector" ref={dropdownRef}>
            <div className="header-facility-selector-container">
                {isSearchMode ? (
                    <input
                        ref={inputRef}
                        type="text"
                        className="header-facility-input"
                        value={searchTerm}
                        onChange={handleInputChange}
                        onKeyDown={handleInputKeyDown}
                        placeholder="Search facilities..."
                    />
                ) : (
                    <div
                        className="header-facility-text"
                        onClick={handleTextClick}
                        title={selectedFacilityDisplay}
                    >
                        <span className="facility-name">
                            {selectedFacilityDisplay}
                        </span>
                    </div>
                )}
                <button
                    className="header-facility-dropdown-button"
                    onClick={toggleDropdown}
                    type="button"
                >
                    <FontAwesomeIcon
                        icon={faChevronDown}
                        className={`chevron ${isOpen ? 'chevron-up' : ''}`}
                    />
                </button>
            </div>

            {isOpen && (
                <div className="header-facility-dropdown">
                    <ul className="facility-options">
                        {isSearchLoading ? (
                            <li>
                                <div className="facility-option loading">
                                    <FontAwesomeIcon
                                        icon={faSpinner}
                                        spin
                                        className="loading-spinner"
                                    />
                                    <span>Searching facilities...</span>
                                </div>
                            </li>
                        ) : Object.keys(facilitiesByCountry).length > 0 ? (
                            Object.entries(facilitiesByCountry).map(
                                ([countryId, { countryName, facilities }]) => (
                                    <React.Fragment key={countryId}>
                                        {/* Facilities for this country */}
                                        {facilities.map(
                                            (
                                                facility: IfacilityWithoutBuildings & {
                                                    option: Ioption;
                                                }
                                            ) => {
                                                const isAssigned = userFacilityIds.includes(
                                                    facility.id
                                                );
                                                const isSelected =
                                                    selectedFacilityID ===
                                                    facility.id;

                                                return (
                                                    <li key={facility.id}>
                                                        <button
                                                            type="button"
                                                            className={`facility-option ${
                                                                isSelected
                                                                    ? 'selected'
                                                                    : ''
                                                            } ${
                                                                isAssigned
                                                                    ? 'assigned'
                                                                    : 'unassigned'
                                                            } ${
                                                                isSearchLoading
                                                                    ? 'disabled'
                                                                    : ''
                                                            }`}
                                                            onClick={() =>
                                                                !isSearchLoading &&
                                                                handleFacilitySelect(
                                                                    facility.id
                                                                )
                                                            }
                                                            disabled={
                                                                isSearchLoading
                                                            }
                                                            title={
                                                                facility.option
                                                                    .label
                                                            }
                                                        >
                                                            <span
                                                                className={`facility-option-text ${
                                                                    isAssigned
                                                                        ? 'assigned'
                                                                        : ''
                                                                }`}
                                                            >
                                                                {
                                                                    facility
                                                                        .option
                                                                        .label
                                                                }
                                                            </span>
                                                            {isSelected && (
                                                                <FontAwesomeIcon
                                                                    icon={
                                                                        faCheck
                                                                    }
                                                                    className="facility-check-icon"
                                                                />
                                                            )}
                                                        </button>
                                                    </li>
                                                );
                                            }
                                        )}
                                    </React.Fragment>
                                )
                            )
                        ) : (
                            <li>
                                <div className="facility-option disabled">
                                    No facilities available in selected country
                                </div>
                            </li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default HeaderFacilitySelector;
